import {TrademarkRenewalTrademarkDetails} from '@/components/trademark-renewal/form/types'
import {Jurisdiction} from '@/utils/countries/constants'
import {Currency} from '@/utils/currency'
import {
  billingEntities,
  BillingEntityId,
  calculateBreakpointFeeTotal,
  calculateVatStatus,
  Fee,
  FeeType,
  sumFees,
  sumPrices,
} from '@/utils/pricing'
import {prices} from '@/utils/pricing/prices'
import {buildProductFee} from '@/utils/pricing/product-fee'

import {EntityDataFragment} from '../../graphqlSdk'
import {add, mul} from '../decimals'
import {
  TmRenewalJurisdictionPrice,
  TmRenewalJurisdictionsPrice,
  TmRenewalPrice,
  TmRenewalTrademarkPrice,
} from './types'

const calculateClassesFee = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  feeType: FeeType,
  numOfClasses: number,
) =>
  calculateBreakpointFeeTotal(
    feeType === 'legal' ? prices.renewalCategoryMargins : prices.renewalCategoryFees,
    jurisdiction,
    currency,
    numOfClasses,
  )

export const getRenewalCategoryFees = (
  jurisdiction: Jurisdiction,
  currency: Currency,
): Record<number, number> =>
  (prices.renewalCategoryFees.breakpoints[jurisdiction] ?? []).reduce(
    (acc, point) => ({
      ...acc,
      [point]: add(
        prices.renewalCategoryFees[`${jurisdiction}.${point}`][currency],
        prices.renewalCategoryMargins?.[`${jurisdiction}.${point}`]?.[currency] ?? 0,
      ),
    }),
    {},
  )

export const getRenewalCategoryMargins = (
  jurisdiction: Jurisdiction,
  currency: Currency,
): Record<number, number> =>
  (prices.renewalCategoryMargins.breakpoints[jurisdiction] ?? []).reduce(
    (acc, point) => ({
      ...acc,
      [point]: prices.renewalCategoryMargins[`${jurisdiction}.${point}`][currency],
    }),
    {},
  )

export const getRenewalCategoryFeeTiers = (
  fees: Record<number, number>,
): {point: number; price: number}[] =>
  Object.entries(fees)
    .map(([point, price]) => ({point: Number(point), price}))
    .sort((a, b) => a.point - b.point)

const calculateTmRenewalLegalFees = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  numOfClasses: number,
  vatRate: number,
  numOfTrademarks: number = 1,
): Fee =>
  buildProductFee('renewalRegistrationMargins', jurisdiction, {
    currency,
    numberOfClasses: numOfClasses,
    quantity: numOfTrademarks,
    vatRate,
  })

const calculateTmRenewalRegistrationFees = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  numOfClasses: number,
  vatRate: number,
  numOfTrademarks: number = 1,
): Fee =>
  buildProductFee('renewalRegistrationFees', jurisdiction, {
    currency,
    numberOfClasses: numOfClasses,
    quantity: numOfTrademarks,
    vatRate,
  })

export const calculateRenewalExtraClassesFee = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  numOfClasses: number,
  numOfTrademarks: number = 1,
) => {
  const legalFee = calculateClassesFee(jurisdiction, currency, 'legal', numOfClasses)
  const registrationFee = calculateClassesFee(jurisdiction, currency, 'registration', numOfClasses)

  return mul(add(legalFee, registrationFee), numOfTrademarks)
}

export const calculateTmRenewalJurisdictionPrice = (
  jurisdiction: Jurisdiction,
  currency: Currency,
  numOfClasses?: number,
  numOfTrademarks?: number,
): TmRenewalJurisdictionPrice => {
  const legalFee = calculateTmRenewalLegalFees(
    jurisdiction,
    currency,
    numOfClasses ?? 1,
    0,
    numOfTrademarks ?? 1,
  )
  const registrationFee = calculateTmRenewalRegistrationFees(
    jurisdiction,
    currency,
    numOfClasses ?? 1,
    0,
    numOfTrademarks ?? 1,
  )

  const total = sumFees([legalFee, registrationFee])

  return {
    legalFee,
    registrationFee,
    total,
  }
}

export const calculateTmRenewalPrice = (
  currency: Currency,
  trademark: TrademarkRenewalTrademarkDetails,
  vatRate: number,
): TmRenewalTrademarkPrice => {
  const {jurisdiction, classes_count: numOfClasses} = trademark

  return {
    jurisdiction,
    legalFee: calculateTmRenewalLegalFees(jurisdiction, currency, numOfClasses, vatRate),
    registrationFee: calculateTmRenewalRegistrationFees(
      jurisdiction,
      currency,
      numOfClasses,
      vatRate,
    ),
    trademarkNumber: trademark.registration_number,
    classes: numOfClasses,
  }
}

export const calculateRenewalJurisdictionsPrice = (
  jurisdictions: Jurisdiction[],
  currency: Currency,
  classesByJurisdiction: Partial<Record<Jurisdiction, number>>,
  trademarksByJurisdiction: Partial<Record<Jurisdiction, number>>,
): TmRenewalJurisdictionsPrice => {
  const jurisdictionsPrice = jurisdictions.map((jurisdiction) => {
    const {legalFee, registrationFee, total} = calculateTmRenewalJurisdictionPrice(
      jurisdiction,
      currency,
      classesByJurisdiction[jurisdiction] ?? 1,
      trademarksByJurisdiction[jurisdiction] ?? 1,
    )

    return {jurisdiction, legalFees: legalFee, registrationFees: registrationFee, total}
  })

  return {
    jurisdictions: jurisdictionsPrice,
    legalFeesTotal: sumPrices(jurisdictionsPrice.map(({legalFees}) => legalFees.total)),
    registrationFeesTotal: sumPrices(
      jurisdictionsPrice.map(({registrationFees}) => registrationFees.total),
    ),
    total: sumPrices(jurisdictionsPrice.map(({total}) => total)),
  }
}

export const calculateRenewalPrice = (
  currency: Currency,
  trademarks: TrademarkRenewalTrademarkDetails[],
  billedEntity: EntityDataFragment | null | undefined,
  forceVatIncluded: boolean,
  billingEntityId: BillingEntityId = 'SK',
): TmRenewalPrice => {
  const billingEntity = billingEntities[billingEntityId]

  const {vatIncluded, reverseVatCharge} = calculateVatStatus(
    billingEntity,
    billedEntity,
    forceVatIncluded,
  )
  const vatRate = forceVatIncluded || vatIncluded ? billingEntity.vatRate : 0

  const trademarkPrices = trademarks.map((trademark) =>
    calculateTmRenewalPrice(currency, trademark, vatRate),
  )

  const total = sumFees(trademarkPrices.flatMap((price) => [price.legalFee, price.registrationFee]))

  return {
    trademarkPrices,
    currency,
    vatIncluded,
    reverseVatCharge,
    vatRate,
    total,
  }
}
